import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function DeviceEditPage() {
  const { deviceId } = useParams();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    device_name: "",
    imei: "",
    status: "available",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://installmentzone.com/api";

  useEffect(() => {
    const fetchDevice = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE_URL}/devices/view.php?id=${deviceId}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}: Failed to fetch`);

        const result = await response.json();
        if (result.success && result.data) {
          setDevice(result.data);
          setFormData({
            device_name: result.data.device_name || "",
            imei: result.data.imei || "",
            status: result.data.status || "available",
          });
        } else {
          setError(result.error || "Device not found.");
        }
      } catch (err) {
        console.error("Error fetching device:", err);
        setError(err.message || "An error occurred while fetching device details.");
      } finally {
        setLoading(false);
      }
    };

    if (deviceId) fetchDevice();
  }, [deviceId, API_BASE_URL]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/devices/edit.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: deviceId, ...formData }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success("Device updated successfully!");
        setTimeout(() => navigate("/devices"), 1500);
      } else {
        toast.error(result.error || "Failed to update device.");
      }
    } catch (err) {
      console.error("Error updating device:", err);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading)
    return (
      <div
        style={{
          textAlign: "center",
          color: "#fff",
          marginTop: "20%",
          fontSize: "18px",
        }}
      >
        Loading device details...
      </div>
    );

  if (error)
    return (
      <div
        style={{
          textAlign: "center",
          color: "#F87171",
          marginTop: "20%",
          fontSize: "18px",
        }}
      >
        Error: {error}
      </div>
    );

  if (!device)
    return (
      <div
        style={{
          textAlign: "center",
          color: "#fff",
          marginTop: "20%",
          fontSize: "18px",
        }}
      >
        Device not found.
      </div>
    );

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        background: "linear-gradient(135deg, #1E3A8A, #3B82F6, #60A5FA)",
        fontFamily: "Segoe UI, sans-serif",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
      }}
    >
      <ToastContainer theme="dark" />

      <div
        style={{
          background: "rgba(255,255,255,0.15)",
          padding: "30px",
          borderRadius: "15px",
          maxWidth: "550px",
          width: "100%",
          boxShadow: "0 6px 15px rgba(0,0,0,0.2)",
          color: "#fff",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            fontSize: "26px",
            fontWeight: "600",
            color: "#fff",
            marginBottom: "25px",
          }}
        >
          Edit Device
        </h2>

        <form onSubmit={handleFormSubmit}>
          <div style={{ marginBottom: "20px" }}>
            <label
              htmlFor="device_name"
              style={{ display: "block", fontWeight: "600", marginBottom: "6px" }}
            >
              Device Name:
            </label>
            <input
              type="text"
              id="device_name"
              name="device_name"
              value={formData.device_name}
              onChange={handleInputChange}
              required
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid #CBD5E1",
                fontSize: "1rem",
                color: "#1E293B",
              }}
            />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label
              htmlFor="imei"
              style={{ display: "block", fontWeight: "600", marginBottom: "6px" }}
            >
              IMEI:
            </label>
            <input
              type="text"
              id="imei"
              name="imei"
              value={formData.imei}
              onChange={handleInputChange}
              required
              minLength="15"
              maxLength="17"
              placeholder="15-17 digits"
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid #CBD5E1",
                fontSize: "1rem",
                color: "#1E293B",
              }}
            />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label
              htmlFor="status"
              style={{ display: "block", fontWeight: "600", marginBottom: "6px" }}
            >
              Status:
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              required
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid #CBD5E1",
                fontSize: "1rem",
                color: "#1E293B",
              }}
            >
              <option value="available">Available</option>
              <option value="assigned_to_agent">Assigned to Agent</option>
              <option value="assigned_to_customer">Assigned to Customer</option>
            </select>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "25px",
            }}
          >
            <button
              type="button"
              onClick={() => navigate("/devices")}
              disabled={isSubmitting}
              style={{
                background: "#DC2626",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                padding: "10px 20px",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                background: "#16A34A",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                padding: "10px 20px",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DeviceEditPage;
